<template>
  <section v-if="treatment" class="bg-gray-50">

<div class="relative h-[75vh]">
  <img
    :src="treatment.heroImage"
    :alt="treatment.title"
    class="w-full h-full object-cover"
  />

  <div
    class="absolute inset-0
           bg-gradient-to-b
           from-black/70 via-black/40 to-transparent
           flex items-center"
  >
    <div class="max-w-5xl mx-auto px-6 text-white">
      <h1 class="text-4xl md:text-5xl font-bold tracking-tight mb-4">
        {{ treatment.title }}
      </h1>

      <p class="text-lg md:text-xl max-w-2xl mb-6">
        {{ treatment.subtitle }}
      </p>

      <router-link
        to="/book-appointment-dental-clinic-guwahati"
        class="bg-[#924f19] hover:bg-[#7b4215]
               text-white px-6 py-3 rounded-full
               font-semibold transition inline-flex items-center mt-4"
      >
        Book Appointment
      </router-link>
    </div>
  </div>
</div>


    <!-- Content -->
    <div class="max-w-6xl mx-auto px-6 py-24">

      <div class="space-y-20">

        <div v-for="(section, i) in treatment.sections" :key="i" class="bg-white rounded-3xl shadow-sm overflow-hidden">

          <!-- TEXT -->
          <div v-if="section.type === 'text'" class="p-10 md:p-14 max-w-3xl">
            <h2 class="text-2xl md:text-3xl font-semibold mb-8
           tracking-tight leading-tight
           border-l-4 border-teal-500 pl-5">
              {{ section.heading }}
            </h2>

            <div class="text-gray-700 text-lg leading-relaxed space-y-6 whitespace-pre-line"
              v-html="formatContent(section.content)"></div>

          </div>


          <!-- IMAGE / FULL BLEED CAROUSEL -->
          <div v-else-if="section.type === 'image'" class="relative">

            <div class="relative w-full aspect-[4/3] md:aspect-[16/9] bg-white overflow-hidden rounded-2xl">

              <!-- <img :src="section.images[currentSlide]"
                class="w-full h-full object-contain bg-white transition-opacity duration-700" /> -->
              <img :src="section.images[currentSlide]"
                class="w-full h-full object-contain bg-white transition-opacity duration-700" />


              <!-- Left Arrow -->
              <button v-if="section.images.length > 1" @click="prevSlide(section.images.length)" class="absolute left-6 top-1/2 -translate-y-1/2
             bg-white/90 hover:bg-white
             p-3 rounded-full shadow-lg transition">
                ‹
              </button>

              <!-- Right Arrow -->
              <button v-if="section.images.length > 1" @click="nextSlide(section.images.length)" class="absolute right-6 top-1/2 -translate-y-1/2
             bg-white/90 hover:bg-white
             p-3 rounded-full shadow-lg transition">
                ›
              </button>

            </div>

            <div class="space-y-4 mt-4">
              <!-- Pagination dots -->
              <div class="flex justify-center gap-2">
                <span v-for="(img, i) in section.images" :key="i" class="w-2 h-2 rounded-full transition"
                  :class="i === currentSlide ? 'bg-teal-600' : 'bg-gray-300'"></span>
              </div>

              <!-- Caption -->
              <p class="text-sm text-gray-500 text-center px-4">
                {{ section.caption }}
              </p>
            </div>

          </div>

          <!-- GALLERY -->
          <div v-else-if="section.type === 'gallery'" class="grid md:grid-cols-2 gap-8">
            <img v-for="(img, j) in section.images" :key="j" :src="img" class="rounded-2xl shadow-md" />
          </div>

        </div>

      </div>


      <!-- FAQs -->
      <div class="mt-28">

        <h2 class="text-3xl font-semibold mb-12 text-center tracking-tight">
          Frequently Asked Questions
        </h2>

        <div class="max-w-4xl mx-auto space-y-5">
          <div v-for="(faq, i) in treatment.faqs" :key="i" class="bg-white p-8 rounded-2xl shadow-sm
             hover:shadow-md transition">
            <p class="text-lg font-medium">
              {{ faq.q }}
            </p>
            <p class="text-gray-600 mt-3 leading-relaxed">
              {{ faq.a }}
            </p>
          </div>
        </div>

      </div>

      <!-- CTA -->

      <div v-if="treatment.cta" class="mt-16 bg-gradient-to-r from-teal-600 to-cyan-500
         rounded-2xl py-10 px-6 text-center text-white">
        <h3 class="text-2xl font-semibold mb-4 tracking-tight">
          {{ treatment.cta.heading }}
        </h3>

        <p class="max-w-2xl mx-auto text-base leading-snug mb-6 whitespace-pre-line">
          {{ treatment.cta.description }}
        </p>

        <router-link :to="treatment.cta.link" class="inline-block bg-white text-teal-700
           px-8 py-3 rounded-full text-base font-medium
           shadow hover:scale-105 transition">
          {{ treatment.cta.buttonText }}
        </router-link>
      </div>



    </div>
  </section>
</template>

<script>
import treatments from "@/data/treatments";
import { useHead } from "@vueuse/head";
import { computed } from "vue";
import { useRoute } from "vue-router";

export default {
  setup() {
    const route = useRoute();

    const treatment = computed(() =>
      treatments.find(t => t.slug === route.params.slug)
    );

    useHead({
      title: computed(() =>
        treatment.value
          ? `${treatment.value.title} | Magnum Dental Clinic Guwahati`
          : "Dental Treatment | Magnum Dental Clinic Guwahati"
      ),
      meta: [
        {
          name: "description",
          content: computed(() =>
            treatment.value
              ? `${treatment.value.subtitle} — Expert ${treatment.value.title} at Magnum Dental Clinic, Guwahati.`
              : "Expert dental treatments at Magnum Dental Clinic Guwahati."
          ),
        },
        {
          name: "robots",
          content: "index, follow",
        },
        {
          property: "og:title",
          content: computed(() =>
            treatment.value
              ? `${treatment.value.title} | Magnum Dental Clinic Guwahati`
              : "Dental Treatment | Magnum Dental Clinic Guwahati"
          ),
        },
        {
          property: "og:url",
          content: computed(() =>
            `https://www.magnumdentalguwahati.in/treatments/${route.params.slug}`
          ),
        },
      ],
      link: [
        {
          rel: "canonical",
          href: computed(() =>
            `https://www.magnumdentalguwahati.in/treatments/${route.params.slug}`
          ),
        },
      ],
    });
  },

  data() {
    return {
      currentSlide: 0,
      autoSlideTimer: null,
    };
  },

  computed: {
    treatment() {
      return treatments.find(
        t => t.slug === this.$route.params.slug
      );
    },
  },

  methods: {
    nextSlide(total) {
      this.currentSlide = (this.currentSlide + 1) % total;
    },
    prevSlide(total) {
      this.currentSlide =
        (this.currentSlide - 1 + total) % total;
    },
    startAutoSlide() {
      const imageSection = this.treatment?.sections.find(
        s => s.type === "image"
      );

      if (imageSection?.images?.length > 1) {
        this.autoSlideTimer = setInterval(() => {
          this.nextSlide(imageSection.images.length);
        }, 4500); // slow & premium
      }
    },
    formatContent(content) {
      if (!content) return "";

      return content
        // bold **text**
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        // line breaks
        .replace(/\n/g, "<br />");
    },
  },

  mounted() {
    this.startAutoSlide();
  },

  beforeUnmount() {
    clearInterval(this.autoSlideTimer);
  },
};
</script>
